import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import './cart-cross-sell.js';

const GIFT_THRESHOLD = 89;
const FREIGHT_THRESHOLD = 149.90;

const formatBRL = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Math.max(value, 0));

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    window.addEventListener('resize', this.syncViewportHeight);
    window.visualViewport?.addEventListener('resize', this.syncViewportHeight);
    window.visualViewport?.addEventListener('scroll', this.syncViewportHeight);
    this.syncViewportHeight();
    this.refreshProgressBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    window.removeEventListener('resize', this.syncViewportHeight);
    window.visualViewport?.removeEventListener('resize', this.syncViewportHeight);
    window.visualViewport?.removeEventListener('scroll', this.syncViewportHeight);
  }

  syncViewportHeight = () => {
    const visibleHeight = window.visualViewport?.height ?? window.innerHeight;

    if (visibleHeight > 0) {
      this.style.setProperty('--cart-drawer-visible-height', `${Math.round(visibleHeight)}px`);
    }
  };

  #handleCartUpdate = (event) => {
    const source = event?.detail?.data?.source;

    if (this.hasAttribute('auto-open') && source !== 'cart-items-component') {
      this.showDialog();
    }

    this.syncViewportHeight();
    this.refreshProgressBar();
  };

  #isUpdatingProgress = false;

  refreshProgressBar = async () => {
    if (this.#isUpdatingProgress) return;
    this.#isUpdatingProgress = true;

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Não foi possível atualizar o carrinho: ${response.status}`);
      }

      const cart = await response.json();
      if (!this.isConnected) return;

      const currentTotal = cart.total_price / 100;
      const progressPercentage = Math.min((currentTotal / FREIGHT_THRESHOLD) * 100, 100);

      const progressFill = this.querySelector('.progress-fill-enhanced, .progress-fill');
      if (progressFill instanceof HTMLElement) {
        progressFill.style.width = `${progressPercentage}%`;
      }

      const messageElement = this.querySelector('.progress-message');
      if (messageElement instanceof HTMLElement) {
        if (currentTotal < GIFT_THRESHOLD) {
          messageElement.textContent = `+ ${formatBRL(GIFT_THRESHOLD - currentTotal)} para ganhar sachê 🍑`;
        } else if (currentTotal < FREIGHT_THRESHOLD) {
          messageElement.textContent = `Sachê liberado 🍑 • + ${formatBRL(FREIGHT_THRESHOLD - currentTotal)} para frete grátis`;
        } else {
          messageElement.textContent = 'Frete grátis liberado 🚚';
        }
      }

      this.querySelectorAll('.checkpoint[data-value]').forEach((checkpoint) => {
        if (!(checkpoint instanceof HTMLElement)) return;

        const checkpointValue = Number(checkpoint.dataset.value);
        const reached = Number.isFinite(checkpointValue) && currentTotal >= checkpointValue;
        checkpoint.classList.toggle('reached', reached);

        const marker = checkpoint.querySelector('.checkpoint-marker-enhanced');
        if (!(marker instanceof HTMLElement)) return;

        if (checkpoint.dataset.checkpoint === 'gift') {
          marker.textContent = reached ? '✓' : '🍑';
        } else if (checkpoint.dataset.checkpoint === 'freight') {
          marker.textContent = reached ? '✓' : '🚚';
        }
      });
    } catch (error) {
      console.error('Erro ao atualizar a régua de benefícios do carrinho:', error);
    } finally {
      this.#isUpdatingProgress = false;
    }
  };

  open() {
    this.syncViewportHeight();
    this.showDialog();
    this.refreshProgressBar();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Virá: ao atualizar o carrinho pela component-cart-items (excluir/reduzir item),
// apenas atualizamos a régua de benefícios. A reabertura da gaveta é responsabilidade
// exclusiva de component-cart-items.js (fonte única), para evitar corrida de timing
// que fechava a gaveta.
const refreshProgressAfterCartUpdate = (event) => {
  if (event?.detail?.data?.source !== 'cart-items-component') return;
  document.querySelector('cart-drawer-component')?.refreshProgressBar?.();
};

document.addEventListener(CartAddEvent.eventName, () => {
  document.querySelector('cart-drawer-component')?.refreshProgressBar?.();
});
document.addEventListener(CartUpdateEvent.eventName, refreshProgressAfterCartUpdate);
window.addEventListener('pageshow', () => {
  document.querySelector('cart-drawer-component')?.refreshProgressBar?.();
});
